import { _QtLoader } from "@/angular/libs/venus-beta/qtloader";
import Bowser from "bowser";

(function() {
    "use strict";

    angular
        .module("vrm.core")
        .service("RemoteConsole2Service", RemoteConsole2Service);

    /**
     * @constructor
     * @ngInject
     */
    function RemoteConsole2Service(
        locale,
        vrmNotificationsService,
        vrmNotificationType,
        ApiService,
        UserService,
        localStorageService,
        vrmConfig,
        $rootScope
    ) {
        this.setupMobileDetection = function() {
            const browser = Bowser.getParser(window.navigator.userAgent);
            const isMobile = browser.parsedResult.platform.type === "mobile";
            const isTablet = browser.parsedResult.platform.type === "tablet";

            if (isMobile) {
                const el = document.querySelector("#rc2-rotate");
                if (el) {
                    el.classList.add("isMobile");
                }
            }

            return { isMobile, isTablet };
        };

        this.setupErrorHandling = function() {
            const originalErrorHandler = window.onerror;
            window.onerror = function(msg, url, line, col, error) {
                if (url && url.includes("js-agent.newrelic.com")) {
                    return true;
                }
                if (
                    msg &&
                    msg.includes("ChunkLoadError") &&
                    msg.includes("js-agent.newrelic.com")
                ) {
                    return true;
                }

                if (originalErrorHandler) {
                    return originalErrorHandler.call(
                        this,
                        msg,
                        url,
                        line,
                        col,
                        error
                    );
                }
                return false;
            };

            const unhandledRejectionHandler = function(event) {
                if (event.reason && event.reason.message) {
                    const msg = event.reason.message;
                    if (
                        (msg.includes("ChunkLoadError") &&
                        msg.includes("js-agent.newrelic.com"))
                        || msg.includes("js-agent.newrelic.com")
                    ) {
                        event.preventDefault();
                        return;
                    }
                }
            };

            window.addEventListener(
                "unhandledrejection",
                unhandledRejectionHandler
            );

            return function cleanup() {
                window.removeEventListener(
                    "unhandledrejection",
                    unhandledRejectionHandler
                );
                if (originalErrorHandler) {
                    window.onerror = originalErrorHandler;
                }
            };
        };

        this.validatePermissions = function(
            installation,
            $state,
            $stateParams,
            isEmbedded = false
        ) {
            if (!installation.view_permissions.rc_gui_v2) {
                const fallbackState = isEmbedded
                    ? "base.installation.dashboard"
                    : "base.installation-overview";
                $state.go(
                    fallbackState,
                    isEmbedded ? { id: $stateParams.id } : {}
                );

                if (
                    !installation.view_permissions.share &&
                    !$rootScope.userState.isDemo
                ) {
                    locale.ready("core").then(function() {
                        vrmNotificationsService.add({
                            type: vrmNotificationType.ERROR,
                            message: locale.getString(
                                "core.not_allowed_shared_user"
                            )
                        });
                    });
                }
                if (!installation.view_permissions.rc_gui_v2) {
                    locale.ready("core").then(function() {
                        vrmNotificationsService.add({
                            type: vrmNotificationType.ERROR,
                            message: locale.getString(
                                "core.not_supporting_new_rc"
                            )
                        });
                    });
                }
                return false;
            }
            return true;
        };

        this.createQtLoader = function(installation, options = {}) {
            const user = UserService.user;
            const spinner = document.querySelector("#qtspinner");
            const canvas = document.querySelector("#qtcanvas");
            const statusMessage = document.querySelector("#qtstatus");
            const errorMessage = document.querySelector("#errstatus");
            const error = document.querySelector("#rc2error");
            const loading = document.querySelector("#rc2loading");

            if (!spinner || !canvas || !statusMessage) {
                return null;
            }

            const showLoader = options.isEmbedded
                ? function(loaderStatus) {
                      if (spinner && canvas && statusMessage) {
                          spinner.style.display = "flex";
                          canvas.style.display = "none";
                          statusMessage.innerHTML = loaderStatus;
                      }
                  }
                : function(loaderStatus) {
                      if (spinner && canvas && statusMessage) {
                          spinner.style.display = "block";
                          canvas.style.display = "none";
                          statusMessage.innerHTML = loaderStatus + "...";
                      }
                  };

            const showCanvas = function() {
                if (spinner && canvas) {
                    spinner.style.display = "none";
                    canvas.style.display = "block";
                }
            };

            const showExit = options.isEmbedded
                ? function() {
                      statusMessage.innerHTML = "Application exit";
                      if (qtLoader.exitCode !== undefined)
                          statusMessage.innerHTML +=
                              " with code " + qtLoader.exitCode;
                      if (qtLoader.exitText !== undefined)
                          statusMessage.innerHTML +=
                              " (" + qtLoader.exitText + ")";

                      // Reset console for embedded
                      canvas.innerHTML = "";
                      canvas.remove();
                      const newCanvas = document.createElement("div");
                      newCanvas.id = "qtcanvas";
                      newCanvas.style.display = "none";
                      document
                          .querySelector("#qtwrapper")
                          .appendChild(newCanvas);
                      spinner.style.display = "flex";
                  }
                : function() {
                      statusMessage.innerHTML = "Application exit";
                      if (qtLoader.exitCode !== undefined)
                          statusMessage.innerHTML +=
                              " with code " + qtLoader.exitCode;
                      if (qtLoader.exitText !== undefined)
                          statusMessage.innerHTML +=
                              " (" + qtLoader.exitText + ")";
                      spinner.style.display = "block";
                      canvas.style.display = "none";
                  };

            const showError = function(errorText) {
                errorMessage.innerHTML = errorText;
                loading.style.display = "none";
                error.style.display = "block";
                canvas.style.display = "none";
            };

            const qtLoader = new _QtLoader({
                canvasElements: [canvas],
                restartLimit: 5,
                restartMode: "RestartOnExit",
                showLoader: showLoader,
                showError: showError,
                showExit: showExit,
                showCanvas: showCanvas,
                arguments: [
                    "--mqtt",
                    "wss://" +
                        installation.mqtt_webhost +
                        "/mqtt?idUser=" +
                        user.id,
                    "--id",
                    installation.identifier,
                    "--user",
                    `vrmlogin_${vrmConfig.mqttApiEnvironment}_${user.email}`,
                    "--pass",
                    new URLSearchParams(window.location.search).has("demoShare")
                        ? vrmConfig.demoShareToken
                        : localStorageService.get("token")
                ]
            });

            return qtLoader;
        };

        this.setupEventListeners = function(isMobile, isTablet) {
            const EVENT_TYPES_TO_OVERRIDE = ["cut", "copy", "paste", "input"];
            let qtEventListeners = [];
            let addEventOverriden = false;

            function overrideAddEvent() {
                addEventOverriden = true;
                EventTarget.prototype._addEventListener =
                    EventTarget.prototype.addEventListener;

                EventTarget.prototype.addEventListener = function(
                    eventType,
                    eventFunction,
                    eventOptions
                ) {
                    const _eventOptions =
                        eventOptions === undefined ? false : eventOptions;
                    this._addEventListener(
                        eventType,
                        eventFunction,
                        _eventOptions
                    );

                    if (EVENT_TYPES_TO_OVERRIDE.includes(eventType)) {
                        const listener = {
                            target: this,
                            type: eventType,
                            function: eventFunction,
                            options: _eventOptions
                        };
                        qtEventListeners.push(listener);
                    }
                };
            }

            function hideAfterEnter(e) {
                if (e.key !== "Enter") return;
                if (
                    e.target.parentElement === document.body &&
                    e.target.tagName === "INPUT"
                ) {
                    e.target.blur();
                }
            }

            function resizeTimeout() {
                setTimeout(() => {
                    window.dispatchEvent(new Event("resize"));
                }, 200);
            }

            // Add event listeners
            window.addEventListener("orientationchange", resizeTimeout);
            if (!addEventOverriden) {
                overrideAddEvent();
            }
            if (isMobile || isTablet) {
                document.addEventListener("keydown", hideAfterEnter);
            }

            return {
                qtEventListeners,
                addEventOverriden,
                hideAfterEnter,
                resizeTimeout,
                cleanup: function() {
                    window.removeEventListener(
                        "orientationchange",
                        resizeTimeout
                    );
                    document.removeEventListener("keydown", hideAfterEnter);

                    if (addEventOverriden) {
                        EventTarget.prototype.addEventListener =
                            EventTarget.prototype._addEventListener;
                        EventTarget.prototype._addEventListener = undefined;
                    }

                    for (const listener of qtEventListeners) {
                        listener.target.removeEventListener(
                            listener.type,
                            listener.function,
                            listener.options
                        );
                    }
                }
            };
        };

        this.loadRemoteConsole = function(
            installation,
            $stateParams,
            isEmbedded = false,
            useModernLoading = false
        ) {
            // Set initial loading text
            const statusMessage = document.querySelector("#qtstatus");
            if (statusMessage) {
                locale.ready("core").then(function() {
                    statusMessage.innerHTML = locale.getString(
                        "core.rc_downloading",
                        "Downloading UI..."
                    );
                });
            }

            const qtLoader = this.createQtLoader(installation, { isEmbedded });

            if (!qtLoader) {
                return null;
            }

            if (useModernLoading) {
                // Use the modern loading approach with prefetching
                qtLoader.fetchThenCompileWasmWithCredentials(
                    ApiService,
                    $stateParams,
                    installation.guiHash,
                    locale
                );
            }

            qtLoader.loadEmscriptenModule(
                "venus-gui-v2-loader",
                ApiService,
                $stateParams,
                installation.guiHash,
                true,
                locale
            );

            // Log remote console access
            if (!$rootScope.userState.isDemo) {
                ApiService.setOpenedRemoteConsoleVersion($stateParams.id, 2);
            }

            return qtLoader;
        };

        this.addBodyClass = function() {
            document
                .querySelector(".vrm-main__container")
                .classList.add("bodyRC2");
        };

        this.removeBodyClass = function() {
            document
                .querySelector(".vrm-main__container")
                .classList.remove("bodyRC2");
        };
    }
})();
